import { isActiveElementEditable } from "@hubrise/react-components"
import * as React from "react"

import { Outside, Inside, ArrowUp, ArrowDown } from "./Styles"

const HIDE_SCROLLBAR_DELAY = 1000

interface DelayedScrollerProps {
  children: React.ReactNode
}

const DelayedScroller = React.forwardRef(
  ({ children }: DelayedScrollerProps, $insideRef: React.ForwardedRef<HTMLDivElement>): JSX.Element => {
    const $outsideRef = React.useRef<HTMLDivElement>(null)
    const $arrowUpRef = React.useRef<HTMLSpanElement>(null)
    const $arrowDownRef = React.useRef<HTMLSpanElement>(null)

    const [scrollbarVisible, setScrollbarVisible] = React.useState(false)

    if ($insideRef === null || typeof $insideRef === "function")
      throw "Ref is mandatory and it should not be a function"

    React.useLayoutEffect(() => {
      const outside: HTMLDivElement = $outsideRef.current!
      outside.style.width = `${outside.clientWidth}px`
    }, [$outsideRef])

    React.useEffect(() => {
      const outside: HTMLDivElement = $outsideRef.current!
      let hideTimeout: number | null = null

      const clearHideTimeout = () => {
        if (hideTimeout !== null) {
          window.clearTimeout(hideTimeout)
          hideTimeout = null
        }
      }

      const hideAfterDelay = () => {
        hideTimeout = window.setTimeout(() => {
          setScrollbarVisible(false)
          hideTimeout = null
        }, HIDE_SCROLLBAR_DELAY)
      }

      const handleMouseEnter = () => {
        clearHideTimeout()
        setScrollbarVisible(true)
      }

      const handleMouseLeave = hideAfterDelay

      const handleKeyDown = (event: KeyboardEvent) => {
        // Show the scrollbar when the user navigates the list using the arrow keys.
        if (!isActiveElementEditable() && (event.code === "ArrowUp" || event.code === "ArrowDown")) {
          clearHideTimeout()
          setScrollbarVisible(true)
          hideAfterDelay()
        }
      }

      outside.addEventListener("mouseenter", handleMouseEnter)
      outside.addEventListener("mouseleave", handleMouseLeave)
      document.body.addEventListener("keydown", handleKeyDown)

      return () => {
        clearHideTimeout()
        outside.removeEventListener("mouseenter", handleMouseEnter)
        outside.removeEventListener("mouseleave", handleMouseLeave)
        document.body.removeEventListener("keydown", handleKeyDown)
      }
    }, [$outsideRef, setScrollbarVisible])

    const refreshArrows = (): void => {
      const insideEl = $insideRef.current
      if (!insideEl) return

      const scrollable = insideEl.scrollHeight > insideEl.clientHeight

      const showUpArrow = scrollable && !scrollbarVisible && insideEl.scrollTop > 0
      const showDownArrow =
        scrollable && !scrollbarVisible && insideEl.scrollTop < insideEl.scrollHeight - insideEl.clientHeight

      $arrowUpRef.current!.classList.toggle("is-visible", showUpArrow)
      $arrowDownRef.current!.classList.toggle("is-visible", showDownArrow)
    }

    React.useEffect(refreshArrows, [$insideRef, scrollbarVisible])

    return (
      <Outside ref={$outsideRef}>
        <Inside scrollbarVisible={scrollbarVisible} onScroll={refreshArrows} ref={$insideRef}>
          {children}
        </Inside>
        <ArrowUp ref={$arrowUpRef} />
        <ArrowDown ref={$arrowDownRef} />
      </Outside>
    )
  },
)

DelayedScroller.displayName = "DelayedScroller"

export default DelayedScroller
